import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Package, Landmark, TrendingUp, Building2, Wheat, FileText,
  ShieldCheck, BadgeCheck, ClipboardList, Zap, BookOpen, Sparkles, Copy, ArrowRight,
  Trash2, Users, Briefcase, Lock, Presentation, Settings, Telescope, FileSignature,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = {
  Landmark, TrendingUp, Building2, Wheat, FileText, ShieldCheck, BadgeCheck, ClipboardList,
  Package, BookOpen, Users, Briefcase, Lock, Presentation, Settings, Telescope, FileSignature, Zap,
};

export default function MarketplacePack() {
  const { packId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uninstalling, setUninstalling] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/marketplace/installed/${packId}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Pack not found"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [packId]);

  const reinstall = async () => {
    try {
      await api.post("/marketplace/install", { pack_id: packId });
      toast.success("Pack installed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Install failed");
    }
  };

  const uninstall = async () => {
    if (!confirm("Uninstall this pack? All assets it created (briefings, automations, knowledge, prompts) will be removed.")) return;
    setUninstalling(true);
    try {
      const r = await api.post("/marketplace/uninstall", { pack_id: packId });
      const purged = r.data.purged || {};
      const total = Object.values(purged).reduce((a, b) => a + b, 0);
      toast.success(`Pack uninstalled · ${total} assets removed`);
      navigate("/marketplace");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uninstall failed");
    } finally {
      setUninstalling(false);
    }
  };

  const copyPrompt = (p) => {
    navigator.clipboard.writeText(p.prompt);
    toast.success("Prompt copied to clipboard");
  };

  if (loading) {
    return (
      <div className="p-10 text-slate-400 font-mono flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading pack…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-10 text-center">
        <p className="text-slate-400">Pack not found.</p>
        <Link to="/marketplace" className="text-[#D4AF37] text-sm">← Back to Marketplace</Link>
      </div>
    );
  }

  const pack = data.pack;
  const Icon = ICONS[pack.icon] || Package;
  const counts = data.counts || {};
  const assets = data.assets || {};
  const totalAssets = (counts.briefings || 0) + (counts.automations || 0) + (counts.knowledge || 0) + (counts.prompts || 0);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="marketplace-pack-page">
      {/* Back */}
      <button
        onClick={() => navigate("/marketplace")}
        data-testid="back-to-marketplace"
        className="text-[11px] font-mono tracking-widest text-slate-400 hover:text-white flex items-center gap-1.5"
      >
        <ArrowLeft className="w-3 h-3" /> BACK TO MARKETPLACE
      </button>

      {/* Header */}
      <div className="nexus-glass-panel p-6 flex flex-col md:flex-row md:items-start gap-5" data-testid="pack-header">
        <div
          className="w-14 h-14 flex items-center justify-center shrink-0"
          style={{ background: pack.color + "20", border: `1px solid ${pack.color}50` }}
        >
          <Icon className="w-6 h-6" style={{ color: pack.color }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="label-eyebrow">// Installed Pack</span>
            {data.installed && (
              <span className="font-mono text-[9px] tracking-widest text-emerald-400">· ACTIVE</span>
            )}
          </div>
          <h1 className="font-display text-3xl text-white tracking-tight">{pack.name}</h1>
          <p className="text-slate-400 text-sm mt-1 max-w-2xl">{pack.tagline}</p>
          {data.installed_at && (
            <div className="font-mono text-[10px] text-slate-500 tracking-widest mt-2">
              INSTALLED · {new Date(data.installed_at).toLocaleDateString()} · {totalAssets} assets
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {!data.installed && (
            <button
              onClick={reinstall}
              data-testid="reinstall-btn"
              className="text-[11px] font-mono tracking-widest px-3 py-2"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              INSTALL PACK
            </button>
          )}
          {data.installed && (
            <button
              onClick={uninstall}
              disabled={uninstalling}
              data-testid="uninstall-btn"
              className="text-[11px] font-mono tracking-widest px-3 py-2 border border-white/15 text-slate-300 hover:border-red-500/40 hover:text-red-400 flex items-center gap-1.5 disabled:opacity-50"
            >
              {uninstalling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
              UNINSTALL
            </button>
          )}
        </div>
      </div>

      {!data.installed ? (
        <div className="nexus-glass-panel p-10 text-center">
          <Package className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg text-white">This pack is not installed yet</h3>
          <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
            Install it to materialise briefing templates, automation rules, AI prompts and knowledge primers
            directly into your workspace.
          </p>
        </div>
      ) : (
        <>
          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="pack-stats">
            <Stat eyebrow="01 // BRIEFINGS" value={counts.briefings || 0} label="In Engagement Hub" route="/engagement" navigate={navigate} />
            <Stat eyebrow="02 // AUTOMATIONS" value={counts.automations || 0} label="In Automation Hub" route="/automation" navigate={navigate} />
            <Stat eyebrow="03 // KNOWLEDGE" value={counts.knowledge || 0} label="In Knowledge base" route="/knowledge" navigate={navigate} />
            <Stat eyebrow="04 // PROMPTS" value={counts.prompts || 0} label="In Copilot / AI Nexus" route="/copilot" navigate={navigate} />
          </div>

          {/* Quick actions */}
          {pack.quick_actions?.length > 0 && (
            <div data-testid="pack-quick-actions">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
                <span className="label-eyebrow">// Quick actions</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {pack.quick_actions.map((qa, i) => {
                  const QIcon = ICONS[qa.icon] || ArrowRight;
                  return (
                    <button
                      key={i}
                      onClick={() => navigate(qa.route)}
                      data-testid={`quick-action-${i}`}
                      className="text-[11px] font-mono tracking-widest px-3 py-2 border border-[#D4AF37]/30 hover:border-[#D4AF37]/70 hover:bg-[#D4AF37]/5 text-slate-200 flex items-center gap-2"
                    >
                      <QIcon className="w-3 h-3" style={{ color: "var(--gold-400)" }} />
                      {qa.label.toUpperCase()}
                      <ArrowRight className="w-3 h-3 opacity-50" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sections */}
          <Section
            title="Briefing templates"
            count={counts.briefings || 0}
            route="/engagement"
            routeLabel="Open Engagement Hub"
            icon={Landmark}
            navigate={navigate}
            empty="No briefing templates in this pack."
          >
            {assets.briefings?.map((b) => (
              <AssetRow
                key={b.id}
                title={b.title}
                subtitle={`${b.target || "—"} · ${b.objective?.slice(0, 90) || ""}`}
                action={() => navigate("/engagement")}
                actionLabel="Open"
                testid={`asset-briefing-${b.id}`}
              />
            ))}
          </Section>

          <Section
            title="Automation rules"
            count={counts.automations || 0}
            route="/automation"
            routeLabel="Open Automation Hub"
            icon={Zap}
            navigate={navigate}
            empty="No automation rules in this pack."
          >
            {assets.automations?.map((a) => (
              <AssetRow
                key={a.id}
                title={a.name}
                subtitle={`Trigger: ${a.trigger} · Action: ${a.action}`}
                action={() => navigate("/automation")}
                actionLabel="Open"
                testid={`asset-automation-${a.id}`}
              />
            ))}
          </Section>

          <Section
            title="Knowledge primers"
            count={counts.knowledge || 0}
            route="/knowledge"
            routeLabel="Open Knowledge"
            icon={BookOpen}
            navigate={navigate}
            empty="No knowledge primers in this pack."
          >
            {assets.knowledge?.map((k) => (
              <AssetRow
                key={k.id}
                title={k.title}
                subtitle={`${k.category} · ${k.summary?.slice(0, 100) || ""}`}
                action={() => navigate("/knowledge")}
                actionLabel="Open"
                testid={`asset-knowledge-${k.id}`}
              />
            ))}
          </Section>

          <Section
            title="AI prompt templates"
            count={counts.prompts || 0}
            route="/copilot"
            routeLabel="Open Copilot"
            icon={Sparkles}
            navigate={navigate}
            empty="No prompt templates in this pack."
          >
            {assets.prompts?.map((p) => (
              <div key={p.id} className="nexus-card p-3 flex items-start gap-3" data-testid={`asset-prompt-${p.id}`}>
                <Sparkles className="w-3.5 h-3.5 mt-1 shrink-0 text-[#D4AF37]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{p.title}</div>
                  <div className="text-[11px] text-slate-500 mb-2">{p.category} · {p.agent}</div>
                  <pre className="text-[12px] text-slate-300 whitespace-pre-wrap font-body leading-snug bg-[#0A111F]/60 border border-white/5 p-2.5 max-h-32 overflow-y-auto">
                    {p.prompt}
                  </pre>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    onClick={() => copyPrompt(p)}
                    data-testid={`copy-prompt-${p.id}`}
                    className="p-2 border border-white/10 text-slate-400 hover:border-[#D4AF37]/40 hover:text-[#D4AF37]"
                    title="Copy prompt"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  );
}

const Stat = ({ eyebrow, value, label, route, navigate }) => (
  <button
    onClick={() => navigate(route)}
    className="nexus-card p-4 text-left hover:border-[#D4AF37]/40 transition-all group"
  >
    <div className="label-eyebrow">{eyebrow}</div>
    <div className="font-display text-2xl mt-1 text-white">{value}</div>
    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1 group-hover:text-[#D4AF37]">
      {label} <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
    </div>
  </button>
);

const Section = ({ title, count, route, routeLabel, icon: Icon, navigate, children, empty }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
          <span className="label-eyebrow">// {title}</span>
          <span className="font-mono text-[9px] text-slate-500">· {count}</span>
        </div>
        {count > 0 && (
          <button
            onClick={() => navigate(route)}
            className="font-mono text-[10px] tracking-widest text-slate-400 hover:text-[#D4AF37] flex items-center gap-1"
          >
            {routeLabel.toUpperCase()} <ArrowRight className="w-3 h-3" />
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="nexus-card p-4 text-[12px] text-slate-500">{empty}</div>
      ) : (
        <div className="space-y-2">{items}</div>
      )}
    </motion.div>
  );
};

const AssetRow = ({ title, subtitle, action, actionLabel, testid }) => (
  <div className="nexus-card p-3 flex items-center gap-3" data-testid={testid}>
    <div className="w-1 self-stretch shrink-0" style={{ background: "var(--gold-500)" }} />
    <div className="flex-1 min-w-0">
      <div className="text-sm text-white truncate">{title}</div>
      <div className="text-[11px] text-slate-500 truncate">{subtitle}</div>
    </div>
    <button
      onClick={action}
      className="text-[10px] font-mono tracking-widest px-2 py-1 border border-white/10 text-slate-300 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] flex items-center gap-1 shrink-0"
    >
      {actionLabel} <ArrowRight className="w-3 h-3" />
    </button>
  </div>
);
